import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { uiConfig } from '../config';
import { useRoleAccess } from '../hooks/useRoleAccess';
import moment from 'moment';
import { DATE_TIME_FORMAT } from '../constants/dateFormats';

interface TrainingSession {
  id: string; // Mongoose _id transformed to id by toJSON
  _id?: string; // Fallback for backward compatibility
  sessionId: string;
  trackId: string;
  trackName: string;
  userId: string;
  userName: string;
  userEmail: string;
  status: 'in_progress' | 'completed' | 'abandoned';
  // Behavioral score from post-training analysis. null until analysis runs.
  behavioralScore: number | null;
  completionPercentage: number;
  itemCount: number;
  completedItems: number;
  startedAt: string;
  completedAt?: string;
  duration?: number;
}

const TrainingSessions: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin } = useRoleAccess();
  const [sessions, setSessions] = useState<TrainingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(uiConfig.defaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('limit', String(rowsPerPage));

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }

      // Use admin endpoint if admin, otherwise user endpoint
      const endpoint = isAdmin
        ? `/training/admin/sessions?${params.toString()}`
        : `/training/sessions?${params.toString()}`;

      const response = await apiService.get(endpoint);

      if (response.success) {
        setSessions(response.data.sessions || []);
        setTotalCount(response.data.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('Error fetching training sessions:', error);
      setError(error.message || 'Failed to fetch training sessions');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, isAdmin]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleViewSession = (session: TrainingSession) => {
    navigate(`/training/sessions/${session.id || session._id}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'abandoned':
        return 'error';
      default:
        return 'default';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const roundedSeconds = Math.floor(seconds);
    const minutes = Math.floor(roundedSeconds / 60);
    const secs = roundedSeconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return moment(dateString).format(DATE_TIME_FORMAT);
  };

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <IconButton onClick={fetchSessions} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {/* Filters */}
        <Box display="flex" gap={2} mb={3}>
          <TextField
            placeholder="Search by user, track, or session ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="abandoned">Abandoned</MenuItem>
            </Select>
          </FormControl>

        </Box>

        {/* Table */}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {isAdmin && <TableCell>User</TableCell>}
                <TableCell>Track</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Completion</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Started At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isAdmin ? 7 : 6} align="center">
                    No training sessions found.
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((session) => (
                  <TableRow
                    key={session.id || session._id}
                    hover
                    onClick={() => handleViewSession(session)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {isAdmin && (
                      <TableCell>
                        <Box>
                          <Typography variant="body2">{session.userName}</Typography>
                          <Typography variant="caption" color="textSecondary">
                            {session.userEmail}
                          </Typography>
                        </Box>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {session.trackName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={session.status.replace('_', ' ')}
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {session.behavioralScore != null ? (
                        <Chip
                          label={`${session.behavioralScore}%`}
                          color={getScoreColor(session.behavioralScore)}
                          size="small"
                        />
                      ) : (
                        <Typography variant="caption" color="textSecondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {session.completionPercentage}%
                    </TableCell>
                    <TableCell>{formatDuration(session.duration)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(session.startedAt)}</Typography>
                      {session.completedAt && (
                        <Typography variant="caption" color="textSecondary">
                          Ended: {formatDate(session.completedAt)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>
    </Box>
  );
};

export default TrainingSessions;
